import {
  Badge,
  Box,
  Flex,
  Grid,
  HStack,
  Progress,
  Text,
  useColorModeValue,
} from '@chakra-ui/react';

import { HOT_PICKS_PREVIEW_ROWS } from './fixtures/marketingPreviewData';
import type { HotPickPreviewRow } from './fixtures/marketingPreviewData';
import { TierBadgeLite } from './shared/TierBadgeLite';

const SPORT_LABELS: Record<string, string> = {
  nba: 'NBA',
  pfb: 'NFL',
  mlb: 'MLB',
  nhl: 'NHL',
  cfb: 'CFB',
  mbb: 'CBB',
};

function wcsQualityLabel(avgWcs: number): string {
  if (avgWcs >= 75) return 'Strong expert quality';
  if (avgWcs >= 65) return 'Solid expert quality';
  if (avgWcs >= 55) return 'Average expert quality';
  return 'Mixed expert quality';
}

function SignalCardLite({ pick }: { pick: HotPickPreviewRow }) {
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('primary.200', 'primary.600');
  const muted = useColorModeValue('gray.600', 'gray.400');
  const totalAccent = useColorModeValue('primary.600', 'primary.300');
  const pickHeadlineColor = useColorModeValue('gray.900', 'white');
  const pickHeadlineBg = useColorModeValue('blackAlpha.50', 'whiteAlpha.100');
  const metricBoxBg = useColorModeValue('primary.50', 'whiteAlpha.100');
  const metricBoxBorder = useColorModeValue('primary.100', 'primary.700');

  const experts = pick.total_picks;
  const avgWcs = pick.avg_expert_wcs;
  const avgRounded = Math.round(avgWcs * 10) / 10;
  const combinedRounded = pick.credibility_weighted_score;
  const sportLabel =
    SPORT_LABELS[pick.pick_sport] ?? pick.pick_sport.toUpperCase();

  return (
    <Box
      p={2}
      borderRadius="xl"
      borderWidth="2px"
      borderColor={borderColor}
      bg={cardBg}
      boxShadow="md"
    >
      <Flex justify="space-between" align="flex-start" mb={2} gap={2}>
        <Badge colorScheme="purple" fontSize="xs">
          {experts} experts agree
        </Badge>
        <Badge colorScheme="gray" variant="outline" fontSize="xs">
          {sportLabel}
        </Badge>
      </Flex>

      <Box
        mb={1.5}
        px={2}
        py={1}
        borderRadius="md"
        bg={pickHeadlineBg}
        borderWidth="1px"
        borderColor={metricBoxBorder}
      >
        <Text
          fontWeight="extrabold"
          fontSize={{ base: 'md', md: 'lg' }}
          color={pickHeadlineColor}
          lineHeight="short"
        >
          {pick.headline}
        </Text>
      </Box>

      <Box
        mb={2}
        p={1.5}
        borderRadius="lg"
        bg={metricBoxBg}
        borderWidth="1px"
        borderColor={metricBoxBorder}
        w="100%"
      >
        <Text fontSize="xs" color={muted} mb={0.5}>
          Avg WCS
        </Text>
        <Text
          fontSize="xl"
          fontWeight="extrabold"
          color={totalAccent}
          lineHeight="1"
          mb={2}
        >
          {avgRounded}
          <Text as="span" fontSize="lg" fontWeight="semibold" color={muted}>
            /100
          </Text>
        </Text>
        <Progress
          value={Math.min(100, Math.max(0, avgWcs))}
          size="md"
          colorScheme={
            avgWcs >= 70 ? 'green' : avgWcs >= 55 ? 'purple' : 'gray'
          }
          borderRadius="full"
        />
        <Text fontSize="xs" color={muted} mt={2}>
          {wcsQualityLabel(avgWcs)}
        </Text>
      </Box>

      <Flex align="center" justify="space-between" gap={2}>
        <HStack spacing={2} flexWrap="wrap">
          {avgWcs >= 70 ? <TierBadgeLite tier="elite" /> : null}
        </HStack>
        <Text fontSize="xs" color={muted} whiteSpace="nowrap">
          Total WCS {combinedRounded} · {experts} experts
        </Text>
      </Flex>
    </Box>
  );
}

export function HotPicksConsensusPreview() {
  const rows = HOT_PICKS_PREVIEW_ROWS;
  const muted = useColorModeValue('gray.600', 'gray.400');

  return (
    <Box w="100%" minW={0}>
      <Flex align="center" gap={2} mb={2} flexWrap="wrap">
        <Badge
          colorScheme="red"
          variant="solid"
          fontSize="xs"
          borderRadius="sm"
        >
          LIVE
        </Badge>
        <Text
          fontSize="xs"
          fontWeight="bold"
          letterSpacing="0.12em"
          textTransform="uppercase"
          color={muted}
        >
          Top straight bets
        </Text>
      </Flex>
      <Text fontSize="xs" color={muted} mb={4}>
        Hot Pick = multiple tracked experts have the same open pregame pick
        (same player, stat, over/under, and line).
      </Text>

      <Grid
        templateColumns={{
          base: '1fr',
          md: 'repeat(2, 1fr)',
          lg: 'repeat(3, 1fr)',
        }}
        gap={4}
      >
        {rows.map((pick) => (
          <SignalCardLite key={pick.headline} pick={pick} />
        ))}
      </Grid>
    </Box>
  );
}
